/* eslint-disable import/namespace */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './GenderByAge.scss';
import * as BvtIcon from 'bvt-icon';
import { AnalyzeCard } from '@bvt-molecules';
import _ from 'lodash';
import { Table } from 'antd';

/**
 * @typedef {Object} ITypeAge
 * @property {string} age
 * @property {string} ageType
 */

/**
 * @param {Object} props
 * @param {Array<ITypeAge>} typeAge
 * @param {Array} data
 */

/**
 * @author Renta <renta.yustika@bvarta.com>
 * @copyright PT Bhumi Varta Technology
 * @param {...IGenderByAgeData} props
 */

export default function GenderByAge(props) {
  const { data, typeAge } = props;
  const [arrangedData, setArrangedData] = useState([]);
  const columns = [
    {
      title: 'Age',
      dataIndex: 'age',
      render: (age) => {
        const Icon = BvtIcon[`AgeType${age.name}`] || BvtIcon['AgeTypeTeen'];
        return (
          <div className='table-column'>
            <p>
              <Icon />
            </p>
            <p>{age.name}</p>
            <p>{age.desc}</p>
          </div>
        );
      },
    },
    {
      title: 'Male',
      dataIndex: 'male',
      render: (male) => {
        return (
          <div className='table-column' style={{ '--GenderColor': '#00A8FF' }}>
            <p>{male.total}</p>
            <p>{male.persentase}%</p>
          </div>
        );
      },
    },
    {
      title: 'Female',
      dataIndex: 'female',
      render: (female) => {
        return (
          <div className='table-column' style={{ '--GenderColor': '#F425A3' }}>
            <p>{female.total}</p>
            <p>{female.persentase}%</p>
          </div>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      render: (total) => {
        return (
          <div className='table-column' style={{ '--GenderColor': '#12B2A7' }}>
            <p>{total.total}</p>
            <p>{total.persentase}%</p>
          </div>
        );
      },
      sorter: (a, b) => a.total.total - b.total.total,
    },
  ];

  const handlePrintAgeRange = (age) => {
    const filteredParameter = _.filter(typeAge, { age: age.toString() });
    const ageType = filteredParameter[0]?.ageType;
    return {
      name: _.join(
        _.concat(ageType?.charAt(0).toUpperCase(), ageType?.slice(1)),
        ''
      ),
      value: _.join(_.concat(age.toString(), ' - ', (age + 4).toString()), ''),
    };
  };

  useEffect(() => {
    let newObjOfAge = [];
    const thisTags = Object.keys(data[0][Object.keys(data[0])[0]]);
    newObjOfAge = _.map(thisTags, (v, i) => {
      let tempData = {};
      _.map(Object.keys(data[0]), (w) => {
        tempData[w] = {
          total: data[0][w][v]['value'],
          persentase: data[0][w][v]['persentase'],
        };
      });
      return {
        key: i,
        age: {
          name: handlePrintAgeRange(parseInt(v.replace('age', ''))).name,
          desc: handlePrintAgeRange(parseInt(v.replace('age', ''))).value,
        },
        male: tempData['male'],
        female: tempData['female'],
        total: tempData['total'],
      };
    });
    setArrangedData(newObjOfAge);
  }, [typeAge]);

  return (
    <AnalyzeCard
      headerIcon={<BvtIcon.CircleInfo height='1.25rem' width='1.25rem' />}
      headerText='Gender by Age'
    >
      <div className='step-result--gender-by-age--body'>
        <div className='step-result--gender-by-age--content'>
          <Table
            className='step-result--gender-by-age--table'
            columns={columns}
            dataSource={arrangedData}
            pagination={{
              position: ['none'],
            }}
            size='middle'
          />
        </div>
      </div>
    </AnalyzeCard>
  );
}

GenderByAge.propTypes = {
  data: PropTypes.array,
  typeAge: PropTypes.array,
};

GenderByAge.defaultProps = {
  data: [
    {
      male: {
        age0: {
          value: 1200,
          persentase: 49.2,
        },
        age5: {
          value: 1534,
          persentase: 49.2,
        },
        age10: {
          value: 1601,
          persentase: 49.2,
        },
        age15: {
          value: 1491,
          persentase: 49.2,
        },
        age20: {
          value: 1531,
          persentase: 49.2,
        },
        age35: {
          value: 1857,
          persentase: 49.2,
        },
      },
      total: {
        age0: {
          value: 2438,
          persentase: 100,
        },
        age5: {
          value: 3117,
          persentase: 100,
        },
        age10: {
          value: 3253,
          persentase: 100,
        },
        age15: {
          value: 3030,
          persentase: 100,
        },
        age20: {
          value: 3111,
          persentase: 100,
        },
        age35: {
          value: 3774,
          persentase: 100,
        },
      },
      female: {
        age0: {
          value: 1238,
          persentase: 50.8,
        },
        age5: {
          value: 1583,
          persentase: 50.8,
        },
        age10: {
          value: 1652,
          persentase: 50.8,
        },
        age15: {
          value: 1539,
          persentase: 50.8,
        },
        age20: {
          value: 1580,
          persentase: 50.8,
        },
        age35: {
          value: 1917,
          persentase: 50.8,
        },
      },
    },
  ],
  typeAge: [
    {
      age: '0',
      ageType: 'baby',
    },
    {
      age: '5',
      ageType: 'kids',
    },
    {
      age: '10',
      ageType: 'teen',
    },
    {
      age: '15',
      ageType: 'teen',
    },
    {
      age: '20',
      ageType: 'adult',
    },
    {
      age: '25',
      ageType: 'adult',
    },
    {
      age: '30',
      ageType: 'adult',
    },
    {
      age: '35',
      ageType: 'adult',
    },
    {
      age: '40',
      ageType: 'mature',
    },
    {
      age: '45',
      ageType: 'mature',
    },
    {
      age: '50',
      ageType: 'mature',
    },
    {
      age: '55',
      ageType: 'mature',
    },
    {
      age: '60',
      ageType: 'mature',
    },
    {
      age: '65',
      ageType: 'mature',
    },
  ],
};
